import { useRef } from 'react';
import type { DocumentNode } from 'graphql';
import {
	useExperienceStart,
	useExperienceSuccess,
	useExperienceFail,
} from '@atlassian/jira-business-experience-tracking';
import { useRankIssue, type BaseIssueNode } from '@atlassian/jira-business-list-handlers';
import { useFlagService, type FlagConfiguration } from '@atlassian/jira-flags';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { RANK_ISSUE_EXPERIENCE } from '../../common/constants';
import messages from './messages';

export const RANK_ITEM_FAIL_GENERIC_FLAG: FlagConfiguration = {
	type: 'error',
	title: messages.rankItemErrorTitle,
	description: messages.rankItemGenericErrorDescription,
};

export const RANK_ITEM_FAIL_404_FLAG: FlagConfiguration = {
	type: 'error',
	title: messages.rankItemErrorTitle,
	description: messages.rankItem404ErrorDescription,
};

export const RANK_ITEM_FAIL_403_FLAG: FlagConfiguration = {
	type: 'error',
	title: messages.rankItemErrorTitle,
	description: messages.rankItem403ErrorDescription,
};

type QueryVariables = {
	jql: string;
	first: number;
	fieldIds: string[];
};

export const useRankTimelineIssue = ({
	queryVariables,
	query,
}: {
	query: DocumentNode;
	queryVariables: QueryVariables;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const startExperience = useExperienceStart(RANK_ISSUE_EXPERIENCE);
	const markExperienceSuccess = useExperienceSuccess(RANK_ISSUE_EXPERIENCE);
	const markExperienceFailed = useExperienceFail(RANK_ISSUE_EXPERIENCE);
	const issueToRankRef = useRef<BaseIssueNode | null>();

	const { showFlag } = useFlagService();
	const { rank } = useRankIssue({
		query,
		variables: queryVariables,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		onError: (error: any) => {
			if (error.statusCode === 403) {
				showFlag(RANK_ITEM_FAIL_403_FLAG);
			} else if (error.statusCode === 404) {
				showFlag(RANK_ITEM_FAIL_404_FLAG);
			} else {
				showFlag(RANK_ITEM_FAIL_GENERIC_FLAG);
				if (error.statusCode >= 500) {
					markExperienceFailed('Issue rank failed.', error);
					return;
				}
			}
			markExperienceSuccess();

			fireUIAnalytics(createAnalyticsEvent({}), 'timelineRankIssue failure');
		},
		onSuccess: () => {
			markExperienceSuccess();
			fireUIAnalytics(createAnalyticsEvent({}), 'timelineRankIssue success');
			issueToRankRef.current = null;
		},
		onStart: () => {
			startExperience();
		},
	});

	return {
		rankIssue: rank,
	};
};
